import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../api/axios';
import useLocale from '../hooks/useLocale';
import useUpdateBreadcrumbs from '../hooks/useUpdateBreadcrumbs';
import { UserContext } from '../contexts/UserContext';
import './AdminHelpTipsPage.css';

const AdminHelpTipsPage = () => {
  const { user } = useContext(UserContext);
  const { getCurrentLanguage } = useLocale();
  const langUi = getCurrentLanguage();

  const [lang, setLang] = useState(langUi || 'en');
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [search, setSearch] = useState('');

  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const [selectedId, setSelectedId] = useState(null);

  // editor
  const selected = useMemo(
    () => slots.find((s) => s.id === selectedId) || null,
    [slots, selectedId],
  );
  const [title, setTitle] = useState('');
  const [bodyMd, setBodyMd] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedTick, setSavedTick] = useState(false);

  const setBreadcrumbs = useCallback(() => {
    return [
      { label: langUi === 'ru' ? 'Дашборд' : 'Dashboard', url: '/dashboard' },
      { label: langUi === 'ru' ? 'Админ: подсказки' : 'Admin: Help tips', url: '/admin/help-tips' },
    ];
  }, [langUi]);
  useUpdateBreadcrumbs(() => setBreadcrumbs());

  useEffect(() => {
    // keep language selector in sync with UI language initially
    setLang(langUi || 'en');
  }, [langUi]);

  const loadSlots = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await api.get('/admin/help/slots', {
        params: {
          lang,
          only_missing: onlyMissing,
          search: search || undefined,
        },
      });

      const data = Array.isArray(res.data) ? res.data : [];
      setSlots(data);

      // keep selection stable if possible
      if (selectedId) {
        const stillThere = data.some((x) => x.id === selectedId);
        if (!stillThere) setSelectedId(data[0]?.id ?? null);
      } else {
        setSelectedId(data[0]?.id ?? null);
      }
    } catch (e) {
      console.error(e);
      setErr(langUi === 'ru' ? 'Ошибка загрузки подсказок' : 'Failed to load help tips');
    } finally {
      setLoading(false);
    }
  }, [lang, onlyMissing, search, selectedId, langUi]);

  useEffect(() => {
    void loadSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, onlyMissing]); // do not auto-refetch on every keystroke search

  // When selection changes, fill editor from existing tip (if any)
  useEffect(() => {
    setSavedTick(false);
    if (!selected) {
      setTitle('');
      setBodyMd('');
      setVideoUrl('');
      return;
    }
    const tip = selected.tip || null;
    setTitle(tip?.title || '');
    setBodyMd(tip?.body_md || '');
    setVideoUrl(tip?.video_url || '');
  }, [selected]);

  const onSave = async () => {
    if (!selected) return;
    setSaving(true);
    setErr('');
    setSavedTick(false);
    try {
      await api.put('/admin/help/tips', {
        id: selected.id, // string code
        lang,
        title: title || null,
        body_md: bodyMd || null,
        video_url: (videoUrl || '').trim()
          ? /^https?:\/\//i.test(videoUrl.trim()) || videoUrl.trim().startsWith('//')
            ? videoUrl.trim()
            : `https://${videoUrl.trim()}`
          : null,
      });

      setSavedTick(true);
      // reload list so filled/missing badge updates
      await loadSlots();
    } catch (e) {
      console.error(e);
      setErr(langUi === 'ru' ? 'Ошибка сохранения' : 'Failed to save');
    } finally {
      setSaving(false);
      setTimeout(() => setSavedTick(false), 1200);
    }
  };

  const isAdmin = Boolean(user?.is_admin);

  if (!isAdmin) {
    return (
      <div className="admin-help-page">
        <div className="admin-help-card">
          <div className="admin-help-title">
            {langUi === 'ru' ? 'Доступ запрещён' : 'Access denied'}
          </div>
          <div className="admin-help-muted">
            {langUi === 'ru'
              ? 'Эта страница доступна только администраторам.'
              : 'This page is available to administrators only.'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-help-page">
      <div className="admin-help-header">
        <div>
          <div className="admin-help-title">
            {langUi === 'ru' ? 'Подсказки интерфейса' : 'UI Help tips'}
          </div>
          <div className="admin-help-subtitle">
            {langUi === 'ru'
              ? 'Слоты автоматически регистрируются скриптом. Здесь вы заполняете контент.'
              : 'Slots are auto-registered by a script. Fill the content here.'}
          </div>
        </div>

        <button className="admin-help-btn" onClick={loadSlots} disabled={loading}>
          {langUi === 'ru' ? 'Обновить' : 'Refresh'}
        </button>
      </div>

      <div className="admin-help-filters">
        <div className="admin-help-filter">
          <label>{langUi === 'ru' ? 'Язык' : 'Language'}</label>
          <select value={lang} onChange={(e) => setLang(e.target.value)}>
            <option value="ru">ru</option>
            <option value="en">en</option>
            <option value="es">es</option>
            <option value="zh">zh</option>
          </select>
        </div>

        <div className="admin-help-filter admin-help-search">
          <label>{langUi === 'ru' ? 'Поиск' : 'Search'}</label>
          <div className="admin-help-search-row">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={langUi === 'ru' ? 'code или slot_id…' : 'code or slot_id…'}
            />
            <button className="admin-help-btn secondary" onClick={loadSlots} disabled={loading}>
              {langUi === 'ru' ? 'Найти' : 'Search'}
            </button>
          </div>
        </div>

        <label className="admin-help-checkbox">
          <input
            type="checkbox"
            checked={onlyMissing}
            onChange={(e) => setOnlyMissing(e.target.checked)}
          />
          <span>{langUi === 'ru' ? 'Только незаполненные' : 'Only missing'}</span>
        </label>
      </div>

      {err ? <div className="admin-help-error">{err}</div> : null}

      <div className="admin-help-layout">
        <div className="admin-help-list">
          <div className="admin-help-list-head">
            <div className="admin-help-list-title">
              {langUi === 'ru' ? 'Слоты' : 'Slots'}{' '}
              <span className="admin-help-count">{slots.length}</span>
            </div>
          </div>

          {loading ? (
            <div className="admin-help-muted pad">{langUi === 'ru' ? 'Загрузка…' : 'Loading…'}</div>
          ) : slots.length === 0 ? (
            <div className="admin-help-muted pad">
              {langUi === 'ru' ? 'Ничего не найдено' : 'Nothing found'}
            </div>
          ) : (
            <div className="admin-help-items">
              {slots.map((s) => (
                <button
                  key={s.id}
                  className={`admin-help-item ${s.id === selectedId ? 'active' : ''}`}
                  onClick={() => setSelectedId(s.id)}
                  type="button"
                >
                  <div className="admin-help-item-top">
                    <div className="admin-help-item-label">{s.label || s.id}</div>
                    <span className={`admin-help-badge ${s.has_tip ? 'filled' : 'missing'}`}>
                      {s.has_tip
                        ? langUi === 'ru'
                          ? 'Заполнено'
                          : 'Filled'
                        : langUi === 'ru'
                          ? 'Нет'
                          : 'Missing'}
                    </span>
                  </div>
                  <div className="admin-help-item-meta">
                    <span className="mono">{s.id}</span>
                    {s.slot_id ? <span className="admin-help-meta-sep">•</span> : null}
                    {s.slot_id ? <span className="mono">#{s.slot_id}</span> : null}
                  </div>
                  {s.source_file ? (
                    <div className="admin-help-item-file">{s.source_file}</div>
                  ) : null}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="admin-help-editor">
          {!selected ? (
            <div className="admin-help-card">
              <div className="admin-help-muted">
                {langUi === 'ru' ? 'Выберите слот слева' : 'Select a slot on the left'}
              </div>
            </div>
          ) : (
            <div className="admin-help-card">
              <div className="admin-help-editor-head">
                <div>
                  <div className="admin-help-editor-title">{selected.label || selected.id}</div>
                  <div className="admin-help-editor-meta">
                    <span className="mono">{selected.id}</span>
                    {selected.slot_id ? <span className="admin-help-meta-sep">•</span> : null}
                    {selected.slot_id ? (
                      <span className="mono">slot_id #{selected.slot_id}</span>
                    ) : null}
                    {selected.source_file ? <span className="admin-help-meta-sep">•</span> : null}
                    {selected.source_file ? <span>{selected.source_file}</span> : null}
                  </div>
                </div>

                <div className="admin-help-editor-actions">
                  <button className="admin-help-btn" onClick={onSave} disabled={saving}>
                    {saving
                      ? langUi === 'ru'
                        ? 'Сохранение…'
                        : 'Saving…'
                      : langUi === 'ru'
                        ? 'Сохранить'
                        : 'Save'}
                  </button>
                  {savedTick ? (
                    <span className="admin-help-saved">
                      {langUi === 'ru' ? '✓ Сохранено' : '✓ Saved'}
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="admin-help-form">
                <div className="admin-help-field">
                  <label>{langUi === 'ru' ? 'Заголовок' : 'Title'}</label>
                  <input value={title} onChange={(e) => setTitle(e.target.value)} />
                </div>

                <div className="admin-help-field">
                  <label>{langUi === 'ru' ? 'Текст (Markdown)' : 'Text (Markdown)'}</label>
                  <textarea value={bodyMd} onChange={(e) => setBodyMd(e.target.value)} rows={10} />
                </div>

                <div className="admin-help-field">
                  <label>{langUi === 'ru' ? 'Видео URL' : 'Video URL'}</label>
                  <input
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    placeholder="https://…"
                  />
                  <div className="admin-help-muted small">
                    {langUi === 'ru'
                      ? 'Поддерживается YouTube и прямые ссылки на .mp4/.webm (видео показывается прямо в подсказке).'
                      : 'Supports YouTube and direct .mp4/.webm links (video is embedded in the tip).'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminHelpTipsPage;
